import type { AdminGame, AdminGameVersion, ReorderGameVersionsRequest } from '@mcgames/contracts';
import { Badge, Button } from '@mcgames/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useFormatBytes } from '../../lib/format-bytes.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { useIndexerStatus } from '../indexer/indexer-hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import { FileButton } from './FileButton.tsx';
import { FindNzbDialog } from './FindNzbDialog.tsx';
import { useGameMutation } from './game-hooks.ts';
import styles from './Games.module.css';
import type { InstallDraft } from './install-draft.ts';
import { VersionEditor } from './VersionEditor.tsx';
import { addedVersionId, useVersionMutation } from './version-hooks.ts';

export type VersionsTabProps = {
  game: AdminGame;
  /** Unsaved install edits by version id, owned by the editor so they survive a tab switch. */
  drafts: ReadonlyMap<string, InstallDraft>;
  onDraftChange: (versionId: string, draft: InstallDraft | null) => void;
};

/**
 * The game's versions in their order beside the editor of the selected one. The selection lives in
 * the URL (`?tab=versions&version=<id>`); without one, the first version is open.
 */
export function VersionsTab({ game, drafts, onDraftChange }: VersionsTabProps) {
  const { t } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const requested = params.get('version');
  const selected = game.versions.find((version) => version.id === requested) ?? game.versions[0];

  function select(versionId: string) {
    setParams({ tab: 'versions', version: versionId }, { replace: true });
  }

  function forget(versionId: string) {
    onDraftChange(versionId, null);
    if (requested === versionId) setParams({ tab: 'versions' }, { replace: true });
  }

  return (
    <div className={styles['versions']}>
      <div className={styles['versionSide']}>
        {game.versions.length === 0 ? (
          <p className={styles['muted']}>{t('games.versions.empty')}</p>
        ) : (
          <VersionList
            game={game}
            selectedId={selected?.id ?? null}
            onSelect={select}
            onDeleted={forget}
          />
        )}
        <AddVersion game={game} onAdded={select} />
      </div>
      {selected !== undefined && (
        <VersionEditor
          key={selected.id}
          game={game}
          version={selected}
          draft={drafts.get(selected.id) ?? null}
          onDraftChange={(draft) => onDraftChange(selected.id, draft)}
        />
      )}
    </div>
  );
}

type VersionListProps = {
  game: AdminGame;
  selectedId: string | null;
  onSelect: (versionId: string) => void;
  onDeleted: (versionId: string) => void;
};

function VersionList({ game, selectedId, onSelect, onDeleted }: VersionListProps) {
  const { t } = useTranslation('admin');
  const reorder = useGameMutation<ReorderGameVersionsRequest>(game.id, 'PUT', '/versions/order');
  const [deleting, setDeleting] = useState<AdminGameVersion | null>(null);
  // Players get the first available version unless they pick another one.
  const defaultId = game.versions.find((version) => version.available)?.id ?? null;
  const count = game.versions.length;

  function move(index: number, direction: -1 | 1) {
    const ids = game.versions.map((version) => version.id);
    const [id, other] = [ids[index], ids[index + direction]];
    if (id === undefined || other === undefined) return;
    ids[index] = other;
    ids[index + direction] = id;
    reorder.mutate({ versionIds: ids });
  }

  return (
    <>
      <ol className={styles['versionList']} aria-label={t('games.tabs.versions')}>
        {game.versions.map((version, index) => (
          <VersionItem
            key={version.id}
            version={version}
            isSelected={version.id === selectedId}
            isDefault={version.id === defaultId}
            canMoveUp={index > 0 && !reorder.isPending}
            canMoveDown={index < count - 1 && !reorder.isPending}
            onSelect={() => onSelect(version.id)}
            onMove={(direction) => move(index, direction)}
            onDelete={() => setDeleting(version)}
          />
        ))}
      </ol>
      <ErrorText error={reorder.error} />
      {deleting !== null && (
        <DeleteVersionDialog
          game={game}
          version={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            onDeleted(deleting.id);
          }}
        />
      )}
    </>
  );
}

type VersionItemProps = {
  version: AdminGameVersion;
  isSelected: boolean;
  isDefault: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSelect: () => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
};

function VersionItem({
  version,
  isSelected,
  isDefault,
  canMoveUp,
  canMoveDown,
  onSelect,
  onMove,
  onDelete,
}: VersionItemProps) {
  const { t } = useTranslation('admin');
  const formatBytes = useFormatBytes();
  const nameId = useId();
  const status = version.nzb?.status ?? 'none';

  return (
    <li className={[styles['versionItem'], isSelected ? styles['versionSelected'] : ''].join(' ')}>
      <button
        type="button"
        className={styles['versionSelect']}
        aria-current={isSelected ? 'true' : undefined}
        onClick={onSelect}
      >
        <span id={nameId} className={styles['versionName']}>
          {version.name}
        </span>
        <span className={styles['versionMeta']}>
          {version.nzb !== null && <span>{formatBytes(version.nzb.downloadSize)}</span>}
          <span>
            {t(
              version.nzbSourceGuid === null
                ? 'games.versions.source.upload'
                : 'games.versions.source.indexer',
            )}
          </span>
        </span>
        <span className={styles['chips']}>
          {isDefault && <Badge tone="accent">{t('games.versions.default')}</Badge>}
          <Badge tone={status === 'missing' || status === 'error' ? 'danger' : 'default'}>
            {t(`games.nzbBadge.${status}`)}
          </Badge>
          {version.broken && <Badge tone="danger">{t('games.versions.broken')}</Badge>}
        </span>
      </button>
      <div className={styles['versionActions']}>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canMoveUp}
          aria-describedby={nameId}
          onClick={() => onMove(-1)}
        >
          {t('games.versions.moveUp')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canMoveDown}
          aria-describedby={nameId}
          onClick={() => onMove(1)}
        >
          {t('games.versions.moveDown')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className={styles['dangerText']}
          aria-describedby={nameId}
          onClick={onDelete}
        >
          {t('games.versions.delete')}
        </Button>
      </div>
    </li>
  );
}

type DeleteVersionDialogProps = {
  game: AdminGame;
  version: AdminGameVersion;
  onClose: () => void;
  onDeleted: () => void;
};

function DeleteVersionDialog({ game, version, onClose, onDeleted }: DeleteVersionDialogProps) {
  const { t } = useTranslation('admin');
  const remove = useVersionMutation<void>(game.id, version.id, 'DELETE');

  return (
    <ConfirmDialog
      title={t('games.versions.deleteTitle')}
      message={t('games.versions.deleteMessage', { name: version.name })}
      confirmLabel={t('games.versions.deleteConfirm')}
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => remove.mutate(undefined, { onSuccess: onDeleted })}
      onCancel={onClose}
    />
  );
}

/** Uploading an NZB or picking an indexer release adds a version at the end of the list. */
function AddVersion({ game, onAdded }: { game: AdminGame; onAdded: (versionId: string) => void }) {
  const { t } = useTranslation('admin');
  const titleId = useId();
  const add = useGameMutation<FormData>(game.id, 'POST', '/versions');
  const indexer = useIndexerStatus();
  const [finding, setFinding] = useState(false);

  function send(file: File | undefined) {
    if (file === undefined) return;
    const form = new FormData();
    form.append('file', file);
    add.mutate(form, {
      onSuccess: (next) => {
        const id = addedVersionId(game, next);
        if (id !== null) onAdded(id);
      },
    });
  }

  return (
    <section className={styles['addVersion']} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles['subheading']}>
        {t('games.versions.add')}
      </h3>
      <div className={styles['addActions']}>
        <FileButton
          label={t('games.versions.upload')}
          accept=".nzb,application/x-nzb,text/xml"
          disabled={add.isPending}
          onFiles={([file]) => send(file)}
        />
        {indexer.data?.enabled === true && (
          <Button size="sm" variant="secondary" onClick={() => setFinding(true)}>
            {t('games.versions.findNzb')}
          </Button>
        )}
      </div>
      {add.isPending && <p className={styles['hint']}>{t('games.nzb.uploading')}</p>}
      <ErrorText error={add.error} />
      {finding && <FindNzbDialog game={game} onClose={() => setFinding(false)} />}
    </section>
  );
}
